/**
 * A tiptap extension that projects inline comments/reactions onto a read-only
 * scene as ProseMirror decorations — the modern analog of the Draft
 * CompositeDecorator + CommentBlock (components/textEditor/commentBlock.js),
 * without mutating the document.
 *
 * For each comment whose anchor resolves inside this scene's doc it adds:
 *  - an inline decoration over [from, to] with the same classes CommentBlock
 *    uses (`inline-comment-block reaction-selection-<reaction|comment>`, plus
 *    `reaction-active` when it is the active comment), and a data-comment-id so
 *    clicks can be routed;
 *  - a small reaction-icon widget at the start of the range.
 *
 * Clicking a decorated range calls onCommentClick(commentId); the reader wires
 * that to the same Redux actions CommentBlock dispatches (open the sidebar +
 * set the active inline comment).
 */
import {Extension} from '@tiptap/core';
import {Plugin, PluginKey} from '@tiptap/pm/state';
import {Decoration, DecorationSet} from '@tiptap/pm/view';

import {getReactionEmoji} from '../../utils/reactionUtil';
import {commentsToDecorationSpecs} from './sceneCommentMapping';

export const REFRESH_COMMENT_DECORATIONS = 'refreshCommentDecorations';
const pluginKey = new PluginKey('commentDecorations');

function specClass(comment, isActive) {
  const reaction = comment.reaction
    ? comment.reaction.toLowerCase()
    : 'comment';
  return `inline-comment-block reaction-selection-${reaction}${
    isActive ? ' reaction-active' : ''
  }`;
}

function buildDecorations(doc, options) {
  const comments = options.getComments() || [];
  const activeId = options.getActiveCommentId();
  const specs = commentsToDecorationSpecs(doc, comments, options.sceneId);
  const decorations = [];
  specs.forEach(({from, to, comment}) => {
    const isActive = activeId && comment._id === activeId;
    decorations.push(
      Decoration.inline(from, to, {
        class: specClass(comment, isActive),
        'data-comment-id': comment._id
      })
    );
    if (comment.reaction) {
      decorations.push(
        Decoration.widget(from, () => {
          const span = document.createElement('span');
          span.className = 'reactionIcon-inline';
          span.textContent = getReactionEmoji(comment.reaction);
          return span;
        })
      );
    }
  });
  return DecorationSet.create(doc, decorations);
}

export function CommentDecorations(options) {
  const resolved = {
    sceneId: options.sceneId,
    getComments: options.getComments || (() => []),
    getActiveCommentId: options.getActiveCommentId || (() => undefined),
    onCommentClick: options.onCommentClick || (() => {})
  };

  return Extension.create({
    name: 'commentDecorations',
    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: pluginKey,
          state: {
            init: (_config, state) => buildDecorations(state.doc, resolved),
            apply: (tr, old) => {
              if (tr.getMeta(REFRESH_COMMENT_DECORATIONS)) {
                return buildDecorations(tr.doc, resolved);
              }
              return tr.docChanged ? old.map(tr.mapping, tr.doc) : old;
            }
          },
          props: {
            decorations(state) {
              return pluginKey.getState(state);
            },
            handleClick(view, pos, event) {
              const target =
                event.target && event.target.closest
                  ? event.target.closest('[data-comment-id]')
                  : null;
              if (target) {
                resolved.onCommentClick(target.getAttribute('data-comment-id'));
                return true;
              }
              return false;
            }
          }
        })
      ];
    }
  });
}
